import { IconButton, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import React, { useState } from 'react';
import { FoldersListSorting, SortingDirection, SortingOption } from '../../types';

const intl = {
  ariaLabel: 'Sort Folders',
  tooltip: 'Sort Folders by',
  nameAsc: 'Name: A to Z',
  nameDesc: 'Name: Z to A',
  createdDesc: 'Created: Most Recent',
  createdAsc: 'Created: Least Recent',
  editedDesc: 'Edited: Most Recent',
  editedAsc: 'Edited: Least Recent'
};

type SortMenuOption = {
  label: string;
  value: FoldersListSorting;
};

const SORT_MENU_OPTIONS: SortMenuOption[] = [
  { label: intl.nameAsc, value: { option: SortingOption.Alphabetical, direction: SortingDirection.asc } },
  { label: intl.nameDesc, value: { option: SortingOption.Alphabetical, direction: SortingDirection.desc } },
  { label: intl.createdDesc, value: { option: SortingOption.Created, direction: SortingDirection.desc } },
  { label: intl.createdAsc, value: { option: SortingOption.Created, direction: SortingDirection.asc } },
  { label: intl.editedDesc, value: { option: SortingOption.Edited, direction: SortingDirection.desc } },
  { label: intl.editedAsc, value: { option: SortingOption.Edited, direction: SortingDirection.asc } }
];

const isSameSorting = (sortingA: FoldersListSorting, sortingB: FoldersListSorting) =>
  sortingA.option === sortingB.option && sortingA.direction === sortingB.direction;

type FoldersListSortingSelectProps = {
  value: FoldersListSorting;
  dataTest?: string;
  disabled?: boolean;
  onChange: (value: FoldersListSorting) => void;
};

export function FoldersListSortingSelect({
  value,
  dataTest = 'foldersListSorting',
  disabled = false,
  onChange
}: FoldersListSortingSelectProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOptionSelect = (option: SortMenuOption) => {
    onChange(option.value);
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={intl.tooltip}>
        <span>
          <IconButton
            aria-label={intl.ariaLabel}
            aria-haspopup="menu"
            aria-expanded={Boolean(anchorEl)}
            aria-controls="folders-sort-menu"
            data-test={dataTest}
            disabled={disabled}
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
            }}
          >
            <SortIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        id="folders-sort-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        {SORT_MENU_OPTIONS.map((option) => (
          <MenuItem
            key={option.label}
            selected={isSameSorting(value, option.value)}
            onClick={() => {
              handleOptionSelect(option);
            }}
          >
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
